/**
 * Ravencoin Asset Classification
 * Distinguishes between tradeable tokens and NFTs
 */

export type AssetType = 'token' | 'nft';

export interface RavencoinAsset {
  assetName: string;
  quantity: number;
  reissuable: boolean;
  hasIpfs?: boolean;
  ipfsHash?: string;
  [key: string]: any;
}

/**
 * Classify a Ravencoin asset as either a tradeable token or an NFT
 * 
 * NFT Criteria:
 * - Quantity = 1 (unique)
 * - Not reissuable (can't mint more)
 * - Has IPFS hash (contains metadata/artwork)
 * 
 * @param asset - The Ravencoin asset to classify
 * @returns 'nft' if it's a collectible, 'token' if it's fungible/tradeable
 */
export function classifyAsset(asset: RavencoinAsset): AssetType {
  // Check for NFT characteristics
  const isUnique = asset.quantity === 1;
  const isNotReissuable = asset.reissuable === false;
  const hasMetadata = Boolean(asset.hasIpfs || asset.ipfsHash);
  
  // NFT = unique (qty 1) + not reissuable + has IPFS metadata
  if (isUnique && isNotReissuable && hasMetadata) {
    return 'nft';
  }
  
  // Otherwise it's a tradeable token
  return 'token';
}

/**
 * Filter an array of assets by type
 * 
 * @param assets - Array of Ravencoin assets
 * @param type - Filter by 'token', 'nft', or 'all'
 * @returns Filtered array of assets
 */
export function filterAssetsByType(
  assets: RavencoinAsset[], 
  type: 'token' | 'nft' | 'all'
): RavencoinAsset[] {
  if (type === 'all') {
    return assets;
  }
  
  return assets.filter(asset => classifyAsset(asset) === type);
}

/**
 * Get statistics about asset types in a collection
 * 
 * @param assets - Array of Ravencoin assets
 * @returns Object with counts of tokens, NFTs, and total
 */
export function getAssetTypeStats(assets: RavencoinAsset[]): {
  tokens: number;
  nfts: number;
  total: number;
} {
  let tokens = 0;
  let nfts = 0;
  
  for (const asset of assets) {
    if (classifyAsset(asset) === 'nft') {
      nfts++;
    } else {
      tokens++;
    }
  }
  
  return {
    tokens,
    nfts,
    total: assets.length
  };
}

/**
 * Check if an asset is suitable for order book trading
 * (i.e., it's a fungible token, not an NFT)
 * 
 * @param asset - The Ravencoin asset to check
 * @returns true if the asset can be traded on an order book
 */
export function isTradeableOnOrderBook(asset: RavencoinAsset): boolean {
  return classifyAsset(asset) === 'token';
}


